import { Button, Modal, Tabs } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ErpConfig, GeneralConfig } from './Tabs';
import { addWebConfig, getWebConfigs } from 'src/apis/helpdesk/webConfig.api';
import { updateSystemSetting } from 'src/apis/master_data/systemSetting.api';
import { WebConfigEn } from 'src/constants/webConfig.enum';
import { AppContext } from 'src/contexts/app.context';
import { showNotification } from '@martech-solution/utils/utils';
import type { WebConfigAdd } from 'src/types/webConfig.type';
import type { SystemSettingAdd } from 'src/types/master_data/systemSetting.type';
import './Setting.scss';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Setting = ({ open, setOpen }: Props) => {
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [systemConfigData, setSystemConfigData] = useState<SystemSettingAdd[]>([]);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setTheme: setThemeFromContext, setLanguage: setLanguageFromContext } = useContext(AppContext);

  // Fetch web config data
  const { data: webConfigData } = useQuery({
    queryKey: ['webConfig'],
    queryFn: () => getWebConfigs(),
    enabled: open // Chỉ fetch khi modal mở
  });

  const addWebConfigMutation = useMutation({
    mutationFn: (webConfigs: WebConfigAdd[]) => addWebConfig(webConfigs),
    onSuccess: () => {
      // Refresh data sau khi update
      queryClient.invalidateQueries({ queryKey: ['webConfig'] });
    }
  });

  const updateSystemSettingMutation = useMutation({
    mutationFn: (systemSettings: SystemSettingAdd[]) => updateSystemSetting(systemSettings),
    onSuccess: () => {
      // Refresh system settings data sau khi update
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    }
  });

  const handleClose = () => {
    setOpen(false);
    setActiveTab('general');
    setSystemConfigData([]);
  };

  const handleSave = () => {
    const theme = localStorage.getItem(WebConfigEn.theme);
    const language = localStorage.getItem(WebConfigEn.language);

    setIsLoading(true);

    if (activeTab === 'general') {
      // Call webConfig API for GeneralConfig tab
      addWebConfigMutation.mutate(
        [
          {
            key: WebConfigEn.language,
            value: language as string
          },
          {
            key: WebConfigEn.theme,
            value: theme as string
          }
        ],
        {
          onSuccess: () => {
            setThemeFromContext(theme as any);
            setLanguageFromContext(language as any);
            showNotification(t('notification.success.setting'), 'success');
            setIsLoading(false);
            //handleClose();
          },
          onError: () => {
            setIsLoading(false);
          }
        }
      );
    } else if (activeTab === 'erp') {
      // Call systemConfig API for ErpConfig tab
      updateSystemSettingMutation.mutate(systemConfigData, {
        onSuccess: () => {
          showNotification(t('notification.success.setting'), 'success');
          setIsLoading(false);
          //handleClose();
        },
        onError: () => {
          setIsLoading(false);
        }
      });
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const items = [
    {
      key: 'general',
      label: t('setting.general.config'),
      children: <GeneralConfig webConfigData={webConfigData} />
    },
    {
      key: 'erp',
      label: t('setting.erp.config'),
      children: <ErpConfig systemConfigData={systemConfigData} setSystemConfigData={setSystemConfigData} />
    }
  ];

  return (
    <Modal
      title={t('setting.title')}
      open={open}
      onCancel={handleClose}
      width={900}
      centered
      destroyOnClose
      bodyStyle={{ height: 'calc(80vh - 110px)', overflow: 'auto', padding: 0 }}
      footer={[
        <Button key='cancel' onClick={handleCancel} size='large'>
          {t('action.cancel')}
        </Button>,
        <Button key='save' type='primary' onClick={handleSave} loading={isLoading} size='large'>
          {t('action.save')}
        </Button>
      ]}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={items}
        size='large'
        tabPosition='left'
        className='setting-tabs'
      />
    </Modal>
  );
};

export default Setting;
